import { useAuth } from "@/auth/hooks/useAuth";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { ProgramType, ProjectType } from "@/types/dashboard";
import { useLocation, useParams } from "react-router-dom";

export const useScopedDashboardAccess = (ownerCarrierId?: number | null) => {
  const { user } = useAuth();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const normalizedUserId =
    user?.id != null && !Number.isNaN(Number(user.id)) ? Number(user.id) : null;

  const hasCarrierRole = !!user?.roles?.includes("ROLE_CARRIER");
  const hasSubcontractorRole = !!user?.roles?.includes("ROLE_SUBCONTRACTOR");
  const isMixedCarrierSubcontractor = hasCarrierRole && hasSubcontractorRole;
  const isPureSubcontractor = hasSubcontractorRole && !hasCarrierRole;

  const shouldResolveProgramOwner =
    ownerCarrierId == null && location.pathname.startsWith("/dashboard/program/");
  const shouldResolveProjectOwner =
    ownerCarrierId == null && location.pathname.startsWith("/dashboard/project/");

  const programs = useFetchDataV2<ProgramType[]>(
    shouldResolveProgramOwner
      ? `${import.meta.env.VITE_API_ENDPOINT}/info/programs`
      : "",
  );
  const project = useFetchDataV2<ProjectType>(
    shouldResolveProjectOwner && id
      ? `${import.meta.env.VITE_API_ENDPOINT}/project/${id}?excludes=indicator,reporting`
      : "",
  );

  const rawResolvedOwnerCarrierId =
    ownerCarrierId ??
    (shouldResolveProgramOwner
      ? programs?.data?.find((item) => item.id == id)?.carrier?.id ?? null
      : shouldResolveProjectOwner
        ? project?.data?.program?.carrier?.id ?? null
        : null);
  const resolvedOwnerCarrierId =
    rawResolvedOwnerCarrierId != null &&
    !Number.isNaN(Number(rawResolvedOwnerCarrierId))
      ? Number(rawResolvedOwnerCarrierId)
      : null;

  const isOwnerScope =
    resolvedOwnerCarrierId != null &&
    normalizedUserId != null &&
    resolvedOwnerCarrierId === normalizedUserId;
  const hasResolvedOwnerScope =
    resolvedOwnerCarrierId != null && normalizedUserId != null;
  const actsAsSubcontractorForScope =
    hasSubcontractorRole &&
    (isPureSubcontractor ||
      (isMixedCarrierSubcontractor &&
        hasResolvedOwnerScope &&
        resolvedOwnerCarrierId !== normalizedUserId));

  const canManageScope =
    (!!hasCarrierRole && !!isOwnerScope) ||
    (!!actsAsSubcontractorForScope && user?.fullAccess === true);

  const canEditEsgScope =
    (!!hasCarrierRole && !!isOwnerScope) ||
    (!!actsAsSubcontractorForScope &&
      (user?.fullAccess === true || user?.esgWriteAccess === true));

  const isReadOnlyScope =
    (!!actsAsSubcontractorForScope && user?.fullAccess !== true) ||
    (!!hasCarrierRole &&
      user?.coworker === true &&
      user?.fullAccess !== true &&
      !actsAsSubcontractorForScope);

  return {
    ownerCarrierId: resolvedOwnerCarrierId,
    hasCarrierRole,
    hasSubcontractorRole,
    isMixedCarrierSubcontractor,
    isPureSubcontractor,
    hasResolvedOwnerScope,
    isOwnerScope,
    actsAsSubcontractorForScope,
    canManageScope,
    canEditEsgScope,
    isReadOnlyScope,
  };
};

export default useScopedDashboardAccess;
